import "./PublicWebsite.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "antd";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CurrencyFormatter from "../../components/CurrencyFormatter";
import SocialMediaIcon from "../../components/SocialMediaIcon";
import { getPublicWebsite } from "../../network/network";
import {
  applyPublicWebsiteLanguage,
  normalizePublicWebsiteLocale,
  resolvePublicWebsiteLocale,
  restorePublicWebsiteLanguage,
} from "./publicWebsiteLanguage";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  DownOutlined,
  EnvironmentFilled,
  GlobalOutlined,
  MailOutlined,
  MoonOutlined,
  MenuOutlined,
  CloseOutlined,
  PhoneOutlined,
  SearchOutlined,
  SunOutlined,
} from "@ant-design/icons";
import {
  applyPublicWebsiteTheme,
  resolvePublicWebsiteTheme,
} from "./publicWebsiteTheme";

const SOCIAL_LINKS = [
  "facebook",
  "x",
  "youtube",
  "instagram",
  "tiktok",
  "whatsapp",
  "snapchat",
  "tripadvisor",
];

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const getLocalizedText = (item, isRtl, ...keys) => {
  if (!item) return "";

  for (const key of keys) {
    const localizedKey = isRtl ? `${key}_ar` : `${key}_en`;
    if (item[localizedKey]) return item[localizedKey];
    if (item[key]) return item[key];
  }

  return "";
};

const getLocalizedArray = (item, key, isRtl) => {
  if (!item) return [];

  const localizedKey = isRtl ? `${key}_ar` : `${key}_en`;
  if (Array.isArray(item[localizedKey])) return item[localizedKey];
  return ensureArray(item[key]);
};

export const normalizePublicWebsiteData = (apiData = {}) => {
  const company = apiData.company || {};
  const website = apiData.website || {};
  const defaultLanguage = normalizePublicWebsiteLocale(
    website.default_language || website.language || company.language,
  );
  const direction =
    website.direction ||
    company.direction ||
    (String(defaultLanguage).toLowerCase().startsWith("ar") ? "rtl" : "ltr");
  const isRtl = direction === "rtl";

  return {
    defaultLanguage,
    direction,
    company,
    primaryColor: website.primary_color || "#bfd508",
    merchant: {
      name: getLocalizedText(company, isRtl, "business_name", "name"),
      logo: company.logo || "",
      currency: company.currency || "SAR",
      taxRate: Number(company.tax_rate || 0),
      isCalories: Boolean(company.is_calories),
    },
    banners: ensureArray(website.banners)
      .slice()
      .sort(
        (first, second) =>
          Number(first?.order || 0) - Number(second?.order || 0),
      ),
    about: website.about || {},
    contact: website.contact || {},
    socialLinks: website.social_links || {},
    branches: ensureArray(apiData.branches),
    categories: ensureArray(apiData.categories_products),
  };
};

const getProductDescription = (product, isRtl) =>
  getLocalizedText(product, isRtl, "description", "desc");

const getProductPrice = (product) => Number(product?.price || 0);

const getBranchAddress = (branch, isRtl) =>
  getLocalizedText(branch, isRtl, "business_address", "address");

const getVisibleCategories = (site) =>
  site.categories
    .map((category) => ({
      ...category,
      products: ensureArray(category.products),
    }))
    .filter((category) => category.products.length > 0);

const PublicWebsite = ({ data }) => {
  const { i18n } = useTranslation();
  const site = useMemo(() => normalizePublicWebsiteData(data), [data]);
  const [locale, setLocale] = useState(() =>
    resolvePublicWebsiteLocale(site.defaultLanguage),
  );
  const [theme, setTheme] = useState(resolvePublicWebsiteTheme);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [activeSection, setActiveSection] = useState("store-top");
  const [search, setSearch] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef(null);

  const t = useMemo(() => i18n.getFixedT(locale), [i18n, locale]);

  useEffect(() => {
    const restoredLocale = restorePublicWebsiteLanguage(site.defaultLanguage);
    setLocale((currentLocale) =>
      currentLocale === restoredLocale ? currentLocale : restoredLocale,
    );
  }, [site.defaultLanguage]);

  const isRtl = locale === "ar";
  const isDarkMode = theme === "dark";
  const direction = isRtl ? "rtl" : "ltr";
  const merchantName =
    getLocalizedText(site.company, isRtl, "business_name", "name") ||
    site.merchant.name ||
    t("public_website_online_store");

  const visibleBanners = useMemo(
    () =>
      site.banners.filter(
        (banner) =>
          banner?.image ||
          getLocalizedText(banner, isRtl, "title") ||
          getLocalizedText(banner, isRtl, "label", "tag") ||
          getLocalizedText(banner, isRtl, "description"),
      ),
    [isRtl, site.banners],
  );

  useEffect(() => {
    const previousTitle = document.title;
    if (merchantName) document.title = merchantName;

    return () => {
      document.title = previousTitle;
    };
  }, [merchantName]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const closeDesktopMenu = () => {
      if (window.innerWidth > 900) setMobileMenuOpen(false);
    };

    closeDesktopMenu();
    window.addEventListener("resize", closeDesktopMenu);

    return () => window.removeEventListener("resize", closeDesktopMenu);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen || typeof document === "undefined") return undefined;

    const handlePointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (visibleBanners.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setBannerIndex((current) => (current + 1) % visibleBanners.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [visibleBanners]);

  useEffect(() => {
    if (bannerIndex >= visibleBanners.length) {
      setBannerIndex(0);
    }
  }, [bannerIndex, visibleBanners.length]);

  const categories = useMemo(() => getVisibleCategories(site), [site]);
  const filteredCategories = useMemo(() => {
    const query = search.trim().toLocaleLowerCase(locale);
    if (!query) return categories;

    return categories
      .map((category) => ({
        ...category,
        products: category.products.filter((product) => {
          const name = getLocalizedText(product, isRtl, "name").toLocaleLowerCase(
            locale,
          );
          const description = getProductDescription(
            product,
            isRtl,
          ).toLocaleLowerCase(locale);
          return name.includes(query) || description.includes(query);
        }),
      }))
      .filter((category) => category.products.length > 0);
  }, [categories, isRtl, locale, search]);

  useEffect(() => {
    if (!categories.length) {
      setActiveCategoryId(null);
      return;
    }

    if (!categories.some((category) => category.id === activeCategoryId)) {
      setActiveCategoryId(categories[0].id);
    }
  }, [activeCategoryId, categories]);

  useEffect(() => {
    if (!search.trim() || !filteredCategories.length) return;

    if (
      !filteredCategories.some((category) => category.id === activeCategoryId)
    ) {
      setActiveCategoryId(filteredCategories[0].id);
    }
  }, [activeCategoryId, filteredCategories, search]);

  const activeBranches = site.branches.filter((branch) => {
    if (branch.is_active === false) return false;

    return Boolean(
      getLocalizedText(branch, isRtl, "name") || getBranchAddress(branch, isRtl),
    );
  });

  const about = site.about || {};
  const aboutHeading = getLocalizedText(about, isRtl, "heading", "title");
  const aboutDescription = getLocalizedText(about, isRtl, "description");
  const aboutHighlights = getLocalizedArray(about, "highlights", isRtl)
    .map((highlight) =>
      typeof highlight === "string"
        ? highlight
        : getLocalizedText(highlight, isRtl, "text", "label", "value"),
    )
    .filter(Boolean);
  const aboutStats = getLocalizedArray(about, "stats", isRtl)
    .map((stat) => ({
      value:
        getLocalizedText(stat, isRtl, "value") || firstDefined(stat?.value, ""),
      label: getLocalizedText(stat, isRtl, "label"),
    }))
    .filter((stat) => stat.value || stat.label);

  const contact = site.contact || {};
  const contactAddress = getLocalizedText(contact, isRtl, "address");
  const workingHours = ensureArray(contact.working_hours || contact.hours);
  const socialLinks = SOCIAL_LINKS.filter((key) => site.socialLinks[key]);
  const hasMenu = categories.length > 0;
  const hasBranches = activeBranches.length > 0;
  const hasAbout = Boolean(
    aboutHeading ||
      aboutDescription ||
      aboutHighlights.length ||
      aboutStats.length,
  );
  const hasWorkingHours = workingHours.some((hours) => {
    const isOpen = Boolean(firstDefined(hours.is_open, hours.enabled, false));
    return Boolean(
      isOpen ||
        hours.opens_at ||
        hours.open ||
        hours.closes_at ||
        hours.close,
    );
  });
  const hasContactFields = Boolean(
    contact.phone_number || contact.phone || contact.email || contactAddress,
  );
  const hasContactDetails = hasContactFields || socialLinks.length > 0;
  const hasContactSection = hasContactDetails || hasWorkingHours;
  const hasSecondaryNav =
    hasMenu || hasBranches || hasAbout || hasContactSection;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateActiveSection = () => {
      if (window.scrollY < 120) {
        setActiveSection("store-top");
        return;
      }

      const sectionIds = [
        hasMenu && "store-menu",
        hasBranches && "store-branches",
        hasAbout && "store-about",
        hasContactSection && "store-contact",
      ].filter(Boolean);
      const offset = window.innerWidth <= 900 ? 88 : 100;
      let nextSection = "store-top";

      sectionIds.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element && element.getBoundingClientRect().top <= offset) {
          nextSection = sectionId;
        }
      });

      if (
        hasContactSection &&
        window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - 80
      ) {
        nextSection = "store-contact";
      }

      setActiveSection((current) =>
        current === nextSection ? current : nextSection,
      );
    };

    updateActiveSection();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, [hasAbout, hasBranches, hasContactSection, hasMenu]);

  useEffect(() => {
    if (!hasMenu || !filteredCategories.length || typeof window === "undefined") {
      return undefined;
    }

    const updateActiveCategory = () => {
      const offset = window.innerWidth <= 900 ? 98 : 130;
      let nextCategoryId = filteredCategories[0]?.id ?? null;

      filteredCategories.forEach((category) => {
        const element = document.getElementById(`store-category-${category.id}`);
        if (element && element.getBoundingClientRect().top <= offset) {
          nextCategoryId = category.id;
        }
      });

      if (nextCategoryId !== null) {
        setActiveCategoryId((current) =>
          current === nextCategoryId ? current : nextCategoryId,
        );
      }
    };

    updateActiveCategory();
    window.addEventListener("scroll", updateActiveCategory, { passive: true });
    window.addEventListener("resize", updateActiveCategory);

    return () => {
      window.removeEventListener("scroll", updateActiveCategory);
      window.removeEventListener("resize", updateActiveCategory);
    };
  }, [filteredCategories, hasMenu]);

  const moveBanner = (step) => {
    if (!visibleBanners.length) return;

    setBannerIndex(
      (current) =>
        (current + step + visibleBanners.length) % visibleBanners.length,
    );
  };

  const scrollToCategory = (categoryId) => {
    setActiveCategoryId(categoryId);
    document
      .getElementById(`store-category-${categoryId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navClassName = (sectionId) =>
    activeSection === sectionId ? "is-active" : "";

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const toggleTheme = () => {
    setTheme((currentTheme) =>
      applyPublicWebsiteTheme(currentTheme === "dark" ? "light" : "dark"),
    );
  };

  const languageItems = [
    { key: "en", label: "English" },
    { key: "ar", label: "العربية" },
  ];

  return (
    <div
      className="public-storefront"
      data-theme={theme}
      dir={direction}
      lang={locale}
      style={{ "--store-brand": site.primaryColor }}
    >
      <nav
        ref={navRef}
        className="public-storefront__nav"
        aria-label={t("public_website_store_navigation")}
      >
        <div className="public-storefront__nav-inner">
          <div className="public-storefront__nav-primary">
            <a className="public-storefront__brand" href="#store-top">
              {site.merchant.logo ? (
                <img src={site.merchant.logo} alt="" />
              ) : (
                <span className="public-storefront__brand-mark">
                  {(merchantName || "S").charAt(0).toUpperCase()}
                </span>
              )}
              <strong>{merchantName}</strong>
            </a>

            <div
              id="public-storefront-navigation"
              className={`public-storefront__nav-links ${
                mobileMenuOpen ? "is-open" : ""
              }`}
            >
              <a
                className={navClassName("store-top")}
                href="#store-top"
                onClick={closeMobileMenu}
              >
                {t("home")}
              </a>
              {hasMenu && (
                <a
                  className={navClassName("store-menu")}
                  href="#store-menu"
                  onClick={closeMobileMenu}
                >
                  {t("menu")}
                </a>
              )}
              {hasBranches && (
                <a
                  className={navClassName("store-branches")}
                  href="#store-branches"
                  onClick={closeMobileMenu}
                >
                  {t("branches")}
                </a>
              )}
              {hasAbout && (
                <a
                  className={navClassName("store-about")}
                  href="#store-about"
                  onClick={closeMobileMenu}
                >
                  {t("website_about")}
                </a>
              )}
              {hasContactSection && (
                <a
                  className={navClassName("store-contact")}
                  href="#store-contact"
                  onClick={closeMobileMenu}
                >
                  {t("website_contact")}
                </a>
              )}
            </div>
          </div>

          <div className="public-storefront__nav-actions">
            <button
              type="button"
              className="public-storefront__theme-toggle"
              aria-label={
                isDarkMode
                  ? t("public_website_use_light_mode")
                  : t("public_website_use_dark_mode")
              }
              aria-pressed={isDarkMode}
              title={
                isDarkMode
                  ? t("public_website_use_light_mode")
                  : t("public_website_use_dark_mode")
              }
              onClick={toggleTheme}
            >
              {isDarkMode ? <SunOutlined /> : <MoonOutlined />}
            </button>

            <Dropdown
              trigger={["click"]}
              placement={isRtl ? "bottomLeft" : "bottomRight"}
              overlayClassName={`public-storefront__language-dropdown ${
                isDarkMode ? "is-dark" : "is-light"
              }`}
              overlayStyle={{ "--store-brand": site.primaryColor }}
              menu={{
                items: languageItems,
                selectable: true,
                selectedKeys: [locale],
                onClick: ({ key }) => {
                  setLocale(applyPublicWebsiteLanguage(key));
                  setMobileMenuOpen(false);
                },
              }}
            >
              <button
                type="button"
                className="public-storefront__language"
                aria-label={t("select_language")}
              >
                <GlobalOutlined />
                <span className="public-storefront__language-label">
                  {locale === "ar" ? "العربية" : "English"}
                </span>
                <span className="public-storefront__language-short">
                  {locale === "ar" ? "AR" : "EN"}
                </span>
                <DownOutlined className="public-storefront__language-arrow" />
              </button>
            </Dropdown>

            {hasSecondaryNav && (
              <button
                type="button"
                className={`public-storefront__mobile-menu-toggle ${
                  mobileMenuOpen ? "is-open" : ""
                }`}
                aria-label={
                  mobileMenuOpen
                    ? t("public_website_close_navigation_menu")
                    : t("public_website_open_navigation_menu")
                }
                aria-expanded={mobileMenuOpen}
                aria-controls="public-storefront-navigation"
                onClick={() => setMobileMenuOpen((current) => !current)}
              >
                {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
              </button>
            )}
          </div>
        </div>
      </nav>

      <main>
        {visibleBanners.length === 0 && (
          <div className="public-storefront__top-anchor" id="store-top" />
        )}
        {visibleBanners.length > 0 && (
          <section
            className="public-storefront__hero"
            id="store-top"
            aria-label={t("public_website_promotions")}
          >
            <div
              className="public-storefront__banner-track"
              style={{
                transform: `translateX(${isRtl ? "" : "-"}${
                  bannerIndex * 100
                }%)`,
              }}
            >
              {visibleBanners.map((banner, index) => {
                const bannerTitle = getLocalizedText(banner, isRtl, "title");
                const bannerLabel = getLocalizedText(
                  banner,
                  isRtl,
                  "label",
                  "tag",
                );
                const bannerDescription = getLocalizedText(
                  banner,
                  isRtl,
                  "description",
                );

                return (
                  <article
                    className="public-storefront__banner"
                    key={banner.id || `${banner.title || "banner"}-${index}`}
                    style={{
                      "--banner-color":
                        banner.background_color ||
                        banner.color ||
                        site.primaryColor,
                    }}
                  >
                    <div className="public-storefront__banner-shade" />
                    <div
                      className={`public-storefront__banner-content ${
                        banner.image ? "has-image" : ""
                      }`}
                    >
                      <div className="public-storefront__banner-copy">
                        {bannerLabel && <span>{bannerLabel}</span>}
                        {bannerTitle && <h1>{bannerTitle}</h1>}
                        {bannerDescription && <p>{bannerDescription}</p>}
                      </div>
                      {banner.image && (
                        <div className="public-storefront__banner-media">
                          <img src={banner.image} alt={bannerTitle || ""} />
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
            {visibleBanners.length > 1 && (
              <>
                <button
                  type="button"
                  className="public-storefront__banner-arrow is-previous"
                  aria-label={t("public_website_previous_banner")}
                  onClick={() => moveBanner(-1)}
                >
                  {isRtl ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
                </button>
                <button
                  type="button"
                  className="public-storefront__banner-arrow is-next"
                  aria-label={t("public_website_next_banner")}
                  onClick={() => moveBanner(1)}
                >
                  {isRtl ? <ArrowLeftOutlined /> : <ArrowRightOutlined />}
                </button>
                <div className="public-storefront__banner-dots">
                  {visibleBanners.map((banner, index) => (
                    <button
                      type="button"
                      aria-label={`${t("public_website_show_banner")} ${index + 1}`}
                      className={bannerIndex === index ? "is-active" : ""}
                      key={banner.id || index}
                      onClick={() => setBannerIndex(index)}
                    />
                  ))}
                </div>
              </>
            )}
          </section>
        )}

        {hasMenu && (
          <section className="public-storefront__menu" id="store-menu">
            <div className="public-storefront__container public-storefront__menu-layout">
              <aside className="public-storefront__categories">
                <span>{t("categories")}</span>
                {categories.map((category) => (
                  <button
                    type="button"
                    className={
                      activeCategoryId === category.id ? "is-active" : ""
                    }
                    key={category.id}
                    onClick={() => scrollToCategory(category.id)}
                  >
                    <span className="public-storefront__category-name">
                      {category.icon && <i>{category.icon}</i>}
                      {getLocalizedText(category, isRtl, "name")}
                    </span>
                    <b>{category.products.length}</b>
                  </button>
                ))}
              </aside>

              <div className="public-storefront__menu-content">
                <div className="public-storefront__search" role="search">
                  <SearchOutlined />
                  <input
                    aria-label={t("public_website_search_menu")}
                    type="search"
                    value={search}
                    placeholder={t("public_website_search_menu")}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      aria-label={t("public_website_clear_search")}
                      onClick={() => setSearch("")}
                    >
                      <CloseCircleFilled />
                    </button>
                  )}
                </div>

                {filteredCategories.length > 0 ? (
                  filteredCategories.map((category) => (
                    <section
                      className="public-storefront__category-section"
                      id={`store-category-${category.id}`}
                      key={category.id}
                    >
                      <header>
                        <h2>{getLocalizedText(category, isRtl, "name")}</h2>
                        <span>
                          {category.products.length} {t("items")}
                        </span>
                      </header>
                      <div className="public-storefront__products">
                        {category.products.map((product) => {
                          const productName = getLocalizedText(
                            product,
                            isRtl,
                            "name",
                          );
                          const productDescription = getProductDescription(
                            product,
                            isRtl,
                          );
                          const soldOut =
                            product.is_available === false ||
                            (product.is_stockable && Number(product.stock) <= 0);

                          return (
                            <article
                              className={`public-storefront__product ${
                                product.image ? "has-image" : "no-image"
                              }`}
                              key={product.id}
                            >
                              {product.image && (
                                <div className="public-storefront__product-image">
                                  <img src={product.image} alt={productName} />
                                  {soldOut && (
                                    <span className="public-storefront__sold-out">
                                      {t("public_website_sold_out")}
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="public-storefront__product-body">
                                {!product.image && soldOut && (
                                  <span className="public-storefront__sold-out is-inline">
                                    {t("public_website_sold_out")}
                                  </span>
                                )}
                                <h3>{productName}</h3>
                                {productDescription && <p>{productDescription}</p>}
                                <div className="public-storefront__product-footer">
                                  <strong>
                                    <CurrencyFormatter
                                      numbercolor="var(--store-ink)"
                                      value={getProductPrice(product)}
                                    />
                                  </strong>
                                  {site.merchant.isCalories &&
                                    product.calories_info && (
                                      <span className="public-storefront__calories">
                                        {t("calories")}: {product.calories_info}
                                      </span>
                                    )}
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  ))
                ) : (
                  <div className="public-storefront__empty-menu">
                    <SearchOutlined />
                    <h2>{t("public_website_no_menu_items")}</h2>
                    <p>{t("public_website_try_another_search")}</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {hasBranches && (
          <section className="public-storefront__branches" id="store-branches">
            <div className="public-storefront__container">
              <div className="public-storefront__section-heading">
                <span className="public-storefront__eyebrow">
                  {t("public_website_our_locations")}
                </span>
                <h2>{t("public_website_visit_branches")}</h2>
              </div>
              <div className="public-storefront__branches-grid">
                {activeBranches.map((branch) => {
                  const branchName = getLocalizedText(branch, isRtl, "name");
                  const branchAddress = getBranchAddress(branch, isRtl);

                  return (
                    <article
                      className="public-storefront__branch-card"
                      key={branch.id}
                    >
                      <div className="public-storefront__branch-card-icon">
                        <EnvironmentFilled />
                      </div>
                      <div>
                        {branchName && <h3>{branchName}</h3>}
                        {branchAddress && <p>{branchAddress}</p>}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {hasAbout && (
          <section className="public-storefront__about" id="store-about">
            <div className="public-storefront__container public-storefront__about-grid">
              <div>
                <span className="public-storefront__eyebrow">{t("public_website_about_us")}</span>
                {aboutHeading && <h2>{aboutHeading}</h2>}
                {aboutDescription && <p>{aboutDescription}</p>}
                {aboutHighlights.length > 0 && (
                  <ul>
                    {aboutHighlights.map((highlight, index) => (
                      <li key={`${highlight}-${index}`}>
                        <CheckCircleFilled /> {highlight}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {aboutStats.length > 0 && (
                <div className="public-storefront__stats">
                  {aboutStats.map((stat, index) => (
                    <div key={`${stat.value}-${stat.label}-${index}`}>
                      {stat.value && <strong>{stat.value}</strong>}
                      {stat.label && <span>{stat.label}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <footer className="public-storefront__footer" id="store-contact">
        <div className="public-storefront__container public-storefront__footer-grid">
          <div className="public-storefront__footer-brand">
            <a className="public-storefront__footer-brand-link" href="#store-top">
              {site.merchant.logo ? (
                <img src={site.merchant.logo} alt="" />
              ) : (
                <span className="public-storefront__footer-brand-mark">
                  {(merchantName || "S").charAt(0).toUpperCase()}
                </span>
              )}
              <div>
                <h2>{merchantName}</h2>
                <span>{t("public_website_online_store")}</span>
              </div>
            </a>

            {socialLinks.length > 0 && (
              <div className="public-storefront__socials">
                {socialLinks.map((key) => (
                  <a
                    href={site.socialLinks[key]}
                    key={key}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={key}
                  >
                    <SocialMediaIcon network={key} />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="public-storefront__footer-section">
            <h3>{t("public_website_quick_links")}</h3>
            <a href="#store-top">{t("home")}</a>
            {hasMenu && <a href="#store-menu">{t("menu")}</a>}
            {hasBranches && <a href="#store-branches">{t("branches")}</a>}
            {hasAbout && <a href="#store-about">{t("website_about")}</a>}
          </div>

          {hasContactFields && (
            <div className="public-storefront__footer-section">
              <h3>{t("website_contact")}</h3>
              {(contact.phone_number || contact.phone) && (
                <a href={`tel:${contact.phone_number || contact.phone}`}>
                  <PhoneOutlined /> <span>{contact.phone_number || contact.phone}</span>
                </a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`}>
                  <MailOutlined /> <span>{contact.email}</span>
                </a>
              )}
              {contactAddress && (
                <span>
                  <EnvironmentFilled /> <span>{contactAddress}</span>
                </span>
              )}
            </div>
          )}

          {hasWorkingHours && (
            <div className="public-storefront__footer-section public-storefront__footer-hours">
              <h3>{t("website_working_hours")}</h3>
              {workingHours.map((hours, index) => {
                const isOpen = firstDefined(
                  hours.is_open,
                  hours.enabled,
                  false,
                );
                const dayKey = String(hours.day || "").toLowerCase();
                const day =
                  (dayKey && t(`website_day_${dayKey}`)) ||
                  getLocalizedText(hours, isRtl, "day", "name", "label") ||
                  dayKey;
                const opensAt = hours.opens_at || hours.open;
                const closesAt = hours.closes_at || hours.close;

                return (
                  <span
                    className="public-storefront__hours-row"
                    key={hours.day || hours.name || index}
                  >
                    <b>{day}</b>
                    <span>
                      {isOpen
                        ? `${opensAt || "--:--"} – ${closesAt || "--:--"}`
                        : t("website_closed")}
                    </span>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div className="public-storefront__footer-bottom">
          <span>
            © {new Date().getFullYear()} {merchantName}
          </span>
          <a
            className="public-storefront__footer-cta"
            href="https://anypos.ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{t("public_website_get_free_website_with")}</span>
            <span className="public-storefront__footer-cta-brand">
              <img src="/favicon.svg" alt="" aria-hidden="true" />
              <b>AnyPOS</b>
            </span>
          </a>
        </div>
      </footer>
    </div>
  );
};

const PublicWebsiteRouteState = ({ loading = false, onRetry }) => (
  <main className="public-storefront-state" aria-busy={loading}>
    {loading ? (
      <>
        <div className="public-storefront-state__spinner" />
        <p>Loading storefront…</p>
      </>
    ) : (
      <>
        <h1>This website is currently unavailable</h1>
        <p>Please check the address or try again later.</p>
        {onRetry && (
          <button type="button" onClick={onRetry}>
            Retry
          </button>
        )}
      </>
    )}
  </main>
);

export const PublicWebsiteRoute = () => {
  const { websiteSlug = "" } = useParams();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    if (!websiteSlug) {
      setState({ status: "unavailable", data: null });
      return () => {
        isMounted = false;
        controller.abort();
      };
    }

    setState({ status: "loading", data: null });

    getPublicWebsite(websiteSlug, { signal: controller.signal })
      .then((response) => {
        if (!isMounted) return;

        const responseData = response?.data?.data;
        if (!responseData) {
          setState({ status: "error", data: null });
          return;
        }

        setState({ status: "ready", data: responseData });
      })
      .catch((error) => {
        if (!isMounted || controller.signal.aborted) return;

        const status = error?.response?.status;
        setState({
          status: status === 403 || status === 404 ? "unavailable" : "error",
          data: null,
        });
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [attempt, websiteSlug]);

  if (state.status === "ready") return <PublicWebsite data={state.data} />;
  if (state.status === "error") {
    return (
      <PublicWebsiteRouteState
        onRetry={() => setAttempt((value) => value + 1)}
      />
    );
  }
  if (state.status === "unavailable") return <PublicWebsiteRouteState />;

  return <PublicWebsiteRouteState loading />;
};

export default PublicWebsite;
